import React from 'react'
import styled, { useTheme, DefaultTheme } from 'styled-components'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { makeStyles, createStyles } from '@mui/styles'
import { ChevronDown, Check } from 'react-feather'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'
import { Flex } from '../Flex'
import { Text } from '../Text'
import { ISelectProps as SelectProps } from './types'
import { baseColors } from '../../theme'
import { InputWrapper } from '../Input/InputAssets'

const useStyles = makeStyles<DefaultTheme, { maxHeight?: number }>(() =>
  createStyles({
    paper: {
      background: `${baseColors.gray8} !important`,
      transformOrigin: 'top center !important',
      borderRadius: '16px !important',
      // width is overwritten by min-width property automatically set by MUI's select width
      // so in order to avoid wider menu items we set width with low value
      width: 10,
      height: ({ maxHeight }) => maxHeight && `${maxHeight}px !important`
    }
  })
)

const StyledSelect = styled(Select)`
  &.MuiInputBase-root {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    font-size: 16px;
    border: 1px solid ${({ theme }) => theme.colors.gray7};
    &.Mui-focused {
      border: 1px solid ${({ theme }) => theme.colors.purple};
    }
    &.Mui-error {
      border: 1px solid ${({ theme }) => theme.colors.red};
    }
    &.MuiOutlinedInput-input {
      padding: 8px 12px;
    }
  }
  fieldset {
    border-radius: 8px;
    border: 0;
  }
`

const StyledLabel = styled(Text)<{ hasIcon?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
`

const DefaultSelect: React.FC<SelectProps> = ({
  items,
  label,
  labelBold,
  labelSideText,
  bottomDescription,
  placeholder = '',
  error,
  errorMsg,
  required,
  name,
  listMaxHeight,
  testId,
  ...props
}) => {
  const { colors } = useTheme()
  const classes = useStyles({ ...(listMaxHeight && { maxHeight: listMaxHeight }) })

  return (
    <InputWrapper {...{ label, labelBold, labelSideText, bottomDescription, error, errorMsg, required }}>
      <StyledSelect
        data-testid={testId}
        error={error}
        name={name}
        IconComponent={(prop) => <ChevronDown width={20} height={20} color={colors.text} {...prop} />}
        MenuProps={{
          classes: {
            paper: classes.paper
          }
        }}
        displayEmpty
        renderValue={(value: any) => {
          if (isNil(value) || isEmpty(value)) {
            return <Text color={colors.white3}>{placeholder}</Text>
          }

          const item = items.find(({ value: v }) => v === value || v === value.value)
          return (
            <Flex alignItems="center" fullWidth gap="8px" data-testid={`list-item-${placeholder}`}>
              {React.isValidElement(item?.startIcon) && item?.startIcon}
              <StyledLabel hasIcon={!isUndefined(item?.startIcon)} clickable>
                {item?.label}
              </StyledLabel>
            </Flex>
          )
        }}
        {...props}
      >
        {items.map((item) => {
          const { value, disabled, startIcon, label, endIcon, comingSoon } = item
          const isSelected = props.value === item.value
          return (
            <MenuItem
              value={value}
              key={value}
              disabled={disabled}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
              data-testid={`list-item-${label}`}
            >
              <Flex alignItems="center" gap="8px" maxWidth={isSelected || comingSoon ? '85%' : '100%'}>
                {React.isValidElement(startIcon) && startIcon}
                <StyledLabel hasIcon={!isUndefined(startIcon)} clickable>
                  {label}
                </StyledLabel>
                {React.isValidElement(endIcon) && endIcon}
              </Flex>
              {isSelected && <Check color={colors.text} />}
            </MenuItem>
          )
        })}
      </StyledSelect>
    </InputWrapper>
  )
}

export default DefaultSelect
